import { useEffect, useState } from "react";
import {
  LoaderFunctionArgs,
  useLoaderData,
  useNavigate,
  useNavigation,
  useParams,
} from "react-router-dom";
import { QueryClient, useQuery } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Checkbox, cn, Select, SelectItem, Textarea } from "@nextui-org/react";
import { Breakpoint, CenButton, useBreakpoint } from "cenero-ui-library";
import { useAuthStore } from "@/store/AuthStore";
import { useBookingsCalendarStore } from "@/store/BookingsCalendarStore";
import { useBookingsCurrentStore } from "@/store/BookingsCurrentStore";
import { useErrorStore } from "@/store/ErrorStore";
import { ApiService } from "@/api/services/ApiService";
import { getDeviceType, getFormattedTime } from "@/lib/helpers";
import { checkoutFormSchema } from "@/lib/schema";
import {
  IApiError,
  IBookingStatusReason,
  ICheckoutFormData,
  ICheckoutRequest,
  IWorkOrder,
} from "@/lib/types";

// ---------------------- Props

interface BookingCheckoutProps {
  parentRoute: string;
}

// ---------------------- API

const workOrderQuery = (bookingId: string) => ({
  queryKey: ["work-order-info", bookingId] as const,
  queryFn: async () => ApiService.bookings.getWorkOrder(bookingId),
});

const statusReasonsQuery = () => ({
  queryKey: ["status-reasons"] as const,
  queryFn: async () => ApiService.bookings.getStatusReasons(),
});

export const loader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const bookingId = params.id;

    if (!bookingId) throw new Error("Booking ID not found");

    try {
      const [workOrder, statusReasons] = await Promise.all([
        queryClient.ensureQueryData<IWorkOrder>(workOrderQuery(bookingId)),
        queryClient.ensureQueryData<IBookingStatusReason[]>(statusReasonsQuery()),
      ]);

      return { workOrder, statusReasons };
    } catch (error) {
      throw error; // Let React Router handle the error
    }
  };

// ---------------------- Component

const BookingCheckout = ({ parentRoute }: BookingCheckoutProps) => {
  // --------- Variables / Hooks

  const breakpoint = useBreakpoint();

  const { id = "" } = useParams();

  const auth = useAuthStore();

  const navigate = useNavigate();

  const isDesktop = breakpoint >= Breakpoint.xl;

  const { workOrder: initialWorkOrder, statusReasons: initialStatusReasons } = useLoaderData() as {
    workOrder: IWorkOrder;
    statusReasons: IBookingStatusReason[];
  };

  const { data: workOrder } = useQuery({
    ...workOrderQuery(id),
    initialData: initialWorkOrder,
  });

  const { data: statusReasons } = useQuery({
    ...statusReasonsQuery(),
    initialData: initialStatusReasons,
  });

  const roomStatus = [
    { key: 1, label: "Functional" },
    { key: 3, label: "Partial" },
    { key: 2, label: "Down" },
  ];

  const navigation = useNavigation();

  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const [isCheckingOutType, setIsCheckingOutType] = useState("");

  const {
    control,
    handleSubmit,
    formState: { isValid },
    reset,
  } = useForm<ICheckoutFormData>({
    resolver: yupResolver(checkoutFormSchema),
    defaultValues: {
      roomStatus: "",
      statusReason: "",
      actionsTaken: "",
      nextSteps: "",
      resolutionDetails: "",
      serviceComplete: false,
      pmVisit: false,
    },
    mode: "onChange",
  });

  const { setError } = useErrorStore();

  // --------- Helpers

  const checkOut = async (type: string, formData: ICheckoutFormData) => {
    try {
      setIsCheckingOut(true);
      setIsCheckingOutType(type);

      let geolocation;

      try {
        geolocation = await ApiService.geolocation.getCurrentLocation();
      } catch (e) {
        const error = new Error("Failed to get location") as IApiError;

        error.problemDetails = {
          title:
            "Unable to get current location. Please ensure location services are enabled in your browser and that you are accessing the application via https and not http.",
        };
        setError(error);
      }

      if (geolocation) {
        const data: ICheckoutRequest = {
          checkoutType: type,
          workOrderId: workOrder.workOrderId ?? "",
          workOrderName: workOrder.workOrderName ?? "",
          ownerId: workOrder.ownerId ?? "",
          bookingId: id,
          userId: auth.userSystemId ?? "",
          userName: auth.userName ?? "",
          latitude: geolocation.latitude,
          longitude: geolocation.longitude,
          address: geolocation.address?.display_name ?? "",
          deviceType: getDeviceType(geolocation.deviceType),
          actionsTaken: formData.actionsTaken,
          nextSteps: formData.nextSteps,
          resolutionDetails: formData.resolutionDetails,
          roomStatus: Number(formData.roomStatus),
          statusReason: Number(formData.statusReason),
          serviceComplete: formData.serviceComplete,
          pmVisit: formData.pmVisit,
        };

        await ApiService.bookings.checkOut(data);

        useBookingsCurrentStore.getState().triggerRefetch();
        useBookingsCalendarStore.getState().triggerRefetch();

        navigate(parentRoute);
      }
    } catch (error) {
      setError(error as IApiError);
    } finally {
      setIsCheckingOut(false);
      setIsCheckingOutType("");
    }
  };

  // --------- Effects

  useEffect(() => {
    if (navigation.state === "idle") {
      reset();
    }
  }, [navigation.state, reset]);

  // Clear any global errors when component unmounts
  useEffect(() => {
    return () => {
      setError(null);
    };
  }, [setError]);

  // --------- JSX

  return (
    <>
      <div className="size-full">
        <form
          className="flex size-full justify-center"
          onSubmit={handleSubmit((data) => checkOut("complete", data))}
        >
          <div
            className={cn(
              "flex size-full flex-col items-center justify-start gap-4 overflow-y-auto overflow-x-hidden px-1",
              [isDesktop && "max-w-[500px]"],
            )}
          >
            {/*******************************************/}
            {/* Booking Information */}
            {/*******************************************/}

            <div className="h-auto w-full text-center">
              <p className="w-full text-xs font-bold text-gray-800 dark:text-gray-200 sm:text-sm">
                {workOrder.serviceAccountName}
              </p>
              <p className="font-base text-xs text-gray-500 dark:text-gray-300 sm:text-sm">
                {workOrder.serviceRequestName}
              </p>
              <p className="font-base text-xs font-bold text-gray-600 dark:text-gray-200 sm:text-sm">
                {`${getFormattedTime(workOrder.localStartTime)} - ${getFormattedTime(workOrder.localEndTime)}`}
              </p>
            </div>

            {/*******************************************/}
            {/* Actions Taken Field */}
            {/*******************************************/}

            <Controller
              name="actionsTaken"
              control={control}
              render={({ field }) => (
                <Textarea
                  {...field}
                  isDisabled={isCheckingOut}
                  label="Actions Taken (Internal)"
                  placeholder="Enter Notes Here"
                  disableAutosize
                  className={cn("rounded-[.875rem]", [isCheckingOut && "opacity-50"])}
                  classNames={{
                    input: "h-20 fix-autocomplete font-medium !text-gray-700 dark:!text-gray-100",
                    label: "font-semibold text-sm !text-gray-900 dark:!text-gray-200",
                  }}
                />
              )}
            />

            {/*******************************************/}
            {/* Next Steps Field */}
            {/*******************************************/}

            <Controller
              name="nextSteps"
              control={control}
              render={({ field }) => (
                <Textarea
                  {...field}
                  isDisabled={isCheckingOut}
                  label="Next Steps (External)"
                  placeholder="Enter Notes Here"
                  disableAutosize
                  className={cn("rounded-[.875rem]", [isCheckingOut && "opacity-50"])}
                  classNames={{
                    input: "h-20 fix-autocomplete font-medium !text-gray-700 dark:!text-gray-100",
                    label: "font-semibold text-sm !text-gray-900 dark:!text-gray-200",
                  }}
                />
              )}
            />

            {/*******************************************/}
            {/* Resolution Details Field */}
            {/*******************************************/}

            <Controller
              name="resolutionDetails"
              control={control}
              render={({ field }) => (
                <Textarea
                  {...field}
                  isDisabled={isCheckingOut}
                  label="Resolution Details (External)"
                  placeholder="Enter Notes Here"
                  disableAutosize
                  className={cn("rounded-[.875rem]", [isCheckingOut && "opacity-50"])}
                  classNames={{
                    input: "h-20 fix-autocomplete font-medium !text-gray-700 dark:!text-gray-100",
                    label: "font-semibold text-sm !text-gray-900 dark:!text-gray-200",
                  }}
                />
              )}
            />

            {/*******************************************/}
            {/* Room Status Select */}
            {/*******************************************/}

            <Controller
              name="roomStatus"
              control={control}
              render={({ field, fieldState }) => (
                <Select
                  {...field}
                  isDisabled={isCheckingOut}
                  label="Room Status"
                  placeholder="Select a status"
                  isRequired
                  className={cn("rounded-[.875rem]", [isCheckingOut && "opacity-50"])}
                  classNames={{
                    value: "fix-autocomplete font-medium !text-gray-700 dark:!text-gray-100",
                    label: "font-semibold text-sm !text-gray-900 dark:!text-gray-200",
                  }}
                  errorMessage={fieldState.error?.message}
                  isInvalid={fieldState.invalid}
                >
                  {roomStatus.map((status) => (
                    <SelectItem key={status.key}>{status.label}</SelectItem>
                  ))}
                </Select>
              )}
            />

            {/*******************************************/}
            {/* Status Reason Select */}
            {/*******************************************/}

            {statusReasons && (
              <Controller
                name="statusReason"
                control={control}
                render={({ field, fieldState }) => (
                  <Select
                    {...field}
                    isDisabled={isCheckingOut}
                    label="Status Reason"
                    placeholder="Select a reason"
                    isRequired
                    className={cn("rounded-[.875rem]", [isCheckingOut && "opacity-50"])}
                    classNames={{
                      value: "fix-autocomplete font-medium !text-gray-700 dark:!text-gray-100",
                      label: "font-semibold text-sm !text-gray-900 dark:!text-gray-200",
                    }}
                    errorMessage={fieldState.error?.message}
                    isInvalid={fieldState.invalid}
                  >
                    {statusReasons.map((reason) => (
                      <SelectItem key={reason.attributeValue}>{reason.reason}</SelectItem>
                    ))}
                  </Select>
                )}
              />
            )}

            <div className="my-4 flex h-auto w-full flex-col items-start justify-center gap-7">
              {/*******************************************/}
              {/* Service Complete Checkbox */}
              {/*******************************************/}

              <Controller
                name="serviceComplete"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    isSelected={field.value}
                    onValueChange={field.onChange}
                    isDisabled={isCheckingOut}
                    className={cn("", [isCheckingOut && "opacity-50"])}
                    classNames={{
                      label: "text-sm font-medium !text-gray-700 dark:!text-gray-100",
                    }}
                  >
                    Service Complete?
                  </Checkbox>
                )}
              />

              {/*******************************************/}
              {/* PM Visit Checkbox */}
              {/*******************************************/}

              <Controller
                name="pmVisit"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    isSelected={field.value}
                    onValueChange={field.onChange}
                    isDisabled={isCheckingOut}
                    className={cn("", [isCheckingOut && "opacity-50"])}
                    classNames={{
                      label: "text-sm font-medium !text-gray-700 dark:!text-gray-100",
                    }}
                  >
                    Count as PM Visit?
                  </Checkbox>
                )}
              />
            </div>

            <div className="flex h-auto w-full flex-row items-start justify-center gap-2">
              {/*******************************************/}
              {/* Complete Button */}
              {/*******************************************/}

              <CenButton
                type="submit"
                className={cn("h-12 w-1/2 max-w-56 text-sm sm:text-base", [
                  isCheckingOut && "opacity-50",
                ])}
                color="primary"
                label="Complete"
                variation="solid"
                isDisabled={isCheckingOut || !isValid}
                isLoading={isCheckingOut && isCheckingOutType === "complete"}
              />

              {/*******************************************/}
              {/* Follow Up Button */}
              {/*******************************************/}

              <CenButton
                type="button"
                className={cn("h-12 w-1/2 max-w-56 text-sm sm:text-base", [
                  isCheckingOut && "opacity-50",
                ])}
                color="secondary"
                label="Follow Up"
                onClick={handleSubmit((data) => checkOut("followup", data))}
                variation="solid"
                isDisabled={isCheckingOut || !isValid}
                isLoading={isCheckingOut && isCheckingOutType === "followup"}
              />
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default BookingCheckout;
